import { useEffect, useRef, useState } from "react";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkAddedOutlinedIcon from "@mui/icons-material/BookmarkAddedOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import {
  useCreateGoalTemplateMutation,
  useDeleteGoalTemplateMutation,
  useListGoalTemplatesQuery,
  useUpdateGoalTemplateMutation,
} from "../../../api/goalTemplateApi";
import GoalWizardDrawer from "../../goals/GoalWizard";
import { cls } from "../../hatim/soft/SoftKit";
import { MAX_BULK_GOALS } from "./bulkSteps";
import {
  copyTemplateGoals,
  goalItemMeta,
  itemToWizardInitial,
  removeGoal,
  saveOptions,
  templateMeta,
  upsertGoal,
  wizardToItem,
} from "./goalItems";
import TemplateNameDialog, { ConfirmTemplateDelete } from "./TemplateNameDialog";

const SAVE_LABEL = {
  saveNew: "bulk_save_template",
  update: "bulk_update_template",
  saveAsNew: "bulk_save_as_new_template",
};

export const sectionClass = `px-4 pt-5 pb-1.5 text-xs tracking-wide ${cls.muted}`;

/** Çizgili, kutusuz satır: simge, ad, alt yazı, sağda küçük düğmeler. */
export function LineRow({ icon, title, meta, right, onClick, disabled, active = false }) {
  const body = (
    <>
      <span className={`shrink-0 ${active ? cls.accent : cls.muted}`}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className={`block text-[15px] truncate ${cls.title}`}>{title}</span>
        {meta ? <span className={`block text-xs truncate mt-0.5 ${cls.muted}`}>{meta}</span> : null}
      </span>
    </>
  );
  return (
    <li className={`flex items-center gap-3 px-4 py-2.5 ${cls.row}`}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className="min-w-0 flex-1 flex items-center gap-3 text-left rounded-md disabled:opacity-60"
        >
          {body}
        </button>
      ) : (
        <span className="min-w-0 flex-1 flex items-center gap-3">{body}</span>
      )}
      {right ? <span className="shrink-0 flex items-center">{right}</span> : null}
    </li>
  );
}

/** Metin bağlantısı gibi duran eylem satırı ("Hedef Ekle", "Şablon olarak kaydet"). */
export function ActionRow({ icon, children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left ${cls.accent} ${cls.rowHover} disabled:opacity-50`}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Verilecek hedef listesini kurar (mobil GoalListBuilder): üstte
 * yöneticinin şablonları (sunucunun sırasıyla, en çok kullanılan üstte),
 * altta liste. Şablona basınca liste onun kopyasıyla dolar. Hedef Ekle ortak
 * hedef sihirbazını açar; sihirbaz kaydetmez, yalnız kalemi döndürür.
 * Toplu atama ve kişi ekleme sihirbazları aynı bileşeni gömer.
 */
export default function GoalListBuilder({ goals, onChange, templateId, onTemplateChange }) {
  const { t } = useScriptMode();
  const { data, isLoading, isError, refetch } = useListGoalTemplatesQuery();
  const templates = data?.data?.templates || [];
  const [createTemplate, { isLoading: creating }] = useCreateGoalTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateGoalTemplateMutation();
  const [deleteTemplate, { isLoading: deleting }] = useDeleteGoalTemplateMutation();
  const busy = creating || updating || deleting;

  // { mode: "add" } | { mode: "edit", index }
  const [wizard, setWizard] = useState(null);
  const [wizardError, setWizardError] = useState(null);
  // { kind: "saveNew" } | { kind: "rename", template }
  const [nameState, setNameState] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [notice, setNotice] = useState(null);
  const noticeTimer = useRef(null);
  // İstek sürerken şablon bağı değişmiş olabilir; tamamlanınca en güncel değere bakılır.
  const templateIdRef = useRef(templateId);
  templateIdRef.current = templateId;

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const flash = (text, error = false) => {
    clearTimeout(noticeTimer.current);
    setNotice({ text, error });
    noticeTimer.current = setTimeout(() => setNotice(null), 3000);
  };

  const errorText = (err, fallback) => err?.message || err?.data?.message || t(fallback);

  const pickTemplate = (template) => {
    // Mevcut listenin yerine geçer.
    onChange(copyTemplateGoals(template));
    onTemplateChange(template._id);
  };

  const editIndex = wizard?.mode === "edit" ? wizard.index : null;
  const editing = editIndex !== null ? goals[editIndex] : null;

  const openWizard = (next) => {
    setWizardError(null);
    setWizard(next);
  };

  const submitWizard = (value) => {
    const res = upsertGoal(goals, wizardToItem(value), editIndex);
    if (!res.ok) {
      setWizardError(t("bulk_duplicate_goal"));
      return;
    }
    onChange(res.goals);
    setWizard(null);
  };

  const saveNew = async (name) => {
    const linkedBefore = templateIdRef.current;
    setNameError(null);
    try {
      const res = await createTemplate({ name, goals }).unwrap();
      setNameState(null);
      // Liste bu arada başka bir şablona bağlandıysa o bağ korunur.
      const created = res?.data?.template;
      if (created && templateIdRef.current === linkedBefore) onTemplateChange(created._id);
      flash(t("bulk_template_saved"));
    } catch (err) {
      setNameError(errorText(err, "bulk_template_save_error"));
    }
  };

  const updateCurrent = async () => {
    if (!templateId) return;
    try {
      await updateTemplate({ templateId, goals }).unwrap();
      flash(t("bulk_template_updated"));
    } catch (err) {
      // Şablon bu arada silinmiş olabilir; liste etiket geçersizleşince tazelenir.
      flash(errorText(err, "bulk_template_save_error"), true);
      refetch();
    }
  };

  const rename = async (template, name) => {
    setNameError(null);
    try {
      await updateTemplate({ templateId: template._id, name }).unwrap();
      setNameState(null);
      flash(t("bulk_template_updated"));
    } catch (err) {
      setNameError(errorText(err, "bulk_template_save_error"));
    }
  };

  const remove = async () => {
    const template = toDelete;
    if (!template) return;
    try {
      await deleteTemplate({ templateId: template._id }).unwrap();
      // Liste yerinde kalır; yalnız şablon bağı kopar.
      if (template._id === templateIdRef.current) onTemplateChange(null);
      setToDelete(null);
      flash(t("bulk_template_deleted"));
    } catch (err) {
      setToDelete(null);
      flash(errorText(err, "bulk_template_delete_error"), true);
      refetch();
    }
  };

  const onSave = (option) => {
    if (option === "update") updateCurrent();
    else {
      setNameError(null);
      setNameState({ kind: "saveNew" });
    }
  };

  const submitName = (name) => {
    if (nameState?.kind === "rename") rename(nameState.template, name);
    else saveNew(name);
  };

  const renderTemplates = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-4">
          <CircularProgress size={20} />
        </div>
      );
    }
    if (isError) {
      return (
        <button
          type="button"
          onClick={() => refetch()}
          className={`w-full px-4 py-3 text-sm text-left ${cls.muted}`}
        >
          {t("bulk_templates_error")} · <span className={cls.accent}>{t("retry")}</span>
        </button>
      );
    }
    if (templates.length === 0) {
      return <p className={`px-4 py-2 text-sm ${cls.muted}`}>{t("bulk_templates_empty")}</p>;
    }
    return (
      <ul>
        {templates.map((template) => {
          const active = template._id === templateId;
          return (
            <LineRow
              key={template._id}
              active={active}
              icon={
                active ? (
                  <BookmarkAddedOutlinedIcon fontSize="small" />
                ) : (
                  <BookmarkBorderIcon fontSize="small" />
                )
              }
              title={template.name}
              meta={templateMeta(template, t)}
              onClick={() => pickTemplate(template)}
              disabled={busy}
              right={
                <>
                  <Tooltip title={t("bulk_rename_template")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        aria-label={`${t("bulk_rename_template")} ${template.name}`}
                        onClick={() => {
                          setNameError(null);
                          setNameState({ kind: "rename", template });
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("delete")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        aria-label={`${t("delete")} ${template.name}`}
                        className={cls.danger}
                        onClick={() => setToDelete(template)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              }
            />
          );
        })}
      </ul>
    );
  };

  return (
    <div data-goal-list-builder>
      <h4 className={sectionClass}>{t("bulk_my_templates")}</h4>
      {renderTemplates()}

      <h4 className={sectionClass}>{t("bulk_goals_section")}</h4>
      {goals.length === 0 ? (
        <p className={`px-4 py-2 text-sm ${cls.muted}`}>{t("bulk_goals_empty")}</p>
      ) : (
        <ul>
          {goals.map((item, index) => (
            <LineRow
              key={`${item.label}::${item.activity || "okuma"}`}
              icon={<FlagOutlinedIcon fontSize="small" />}
              title={item.label}
              meta={goalItemMeta(item, t)}
              onClick={() => openWizard({ mode: "edit", index })}
              disabled={busy}
              right={
                <IconButton
                  size="small"
                  disabled={busy}
                  aria-label={`${t("bulk_remove_goal")} ${item.label}`}
                  onClick={() => onChange(removeGoal(goals, index))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            />
          ))}
        </ul>
      )}
      {goals.length > MAX_BULK_GOALS ? (
        <p className={`px-4 pt-2 text-sm ${cls.danger}`}>{t("bulk_too_many_goals", { n: MAX_BULK_GOALS })}</p>
      ) : null}

      <div className="pt-1">
        <ActionRow
          icon={<AddIcon fontSize="small" />}
          onClick={() => openWizard({ mode: "add" })}
          disabled={busy || goals.length >= MAX_BULK_GOALS}
        >
          {t("my_goals_add")}
        </ActionRow>
        {goals.length > 0
          ? saveOptions(templateId, templates).map((option) => (
              <ActionRow
                key={option}
                icon={<SaveOutlinedIcon fontSize="small" />}
                onClick={() => onSave(option)}
                disabled={busy}
              >
                {t(SAVE_LABEL[option])}
              </ActionRow>
            ))
          : null}
      </div>
      {busy ? (
        <div className="flex justify-center py-2">
          <CircularProgress size={18} />
        </div>
      ) : null}
      {notice ? (
        <p role="status" className={`px-4 py-2 text-sm ${notice.error ? cls.danger : cls.accent}`}>
          {notice.text}
        </p>
      ) : null}

      <GoalWizardDrawer
        open={Boolean(wizard)}
        // Eski kalemde eser yok: ad ve biçim korunur, yalnız takip ayarları değişir.
        mode={editing && !editing.workId ? "edit" : "add"}
        initial={editing ? itemToWizardInitial(editing) : undefined}
        title={t(editing ? "gw_title_edit" : "gw_title_add")}
        subtitle={t("bulk_wizard_subtitle")}
        submitLabel={t(editing ? "gw_update" : "manage_template_add_to_list")}
        error={wizardError}
        onSubmit={submitWizard}
        onClose={() => setWizard(null)}
      />
      <TemplateNameDialog
        open={Boolean(nameState)}
        initialName={nameState?.kind === "rename" ? nameState.template.name : ""}
        title={t(nameState?.kind === "rename" ? "bulk_rename_template" : "bulk_template_name")}
        busy={creating || updating}
        error={nameError}
        onCancel={() => setNameState(null)}
        onSubmit={submitName}
      />
      <ConfirmTemplateDelete
        open={Boolean(toDelete)}
        name={toDelete?.name}
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
}
